import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { RootState } from '../redux/store';
import { tokenReceived, logout } from '../features/auth/authSlice';
import { settings } from '../settings';
import {
    Riesgo,
    Examen,
    Establecimiento,
    Prestador,
    Localidad,
    AsignarExamenPayload,
    AsignarPrestadorPayload
} from '../pages/Periodicos/consts/periodicosTypes';

export interface GetExposedEmployeesParams {
    cuil?: string;
    idEstablecimiento?: number;
    idOrganismoCentral?: number;
    nombre?: string;
    apellido?: string;
    estadoGeneral?: number;
    examen?: boolean;
    prestador?: boolean;
    idLocalidad?: number;
    idRiesgo?: number;
    idPrestador?: number;
    skip?: number;
    take?: number;
}

export interface ExportarPeriodicosPayload {
    cuil?: string;
    idEstablecimiento?: number;
    idOrganismoCentral?: number;
    nombre?: string;
    apellido?: string;
    estadoGeneral?: number;
    examen?: boolean;
    prestador?: boolean;
    idPrestador?: number | boolean;
    idLocalidad?: number;
    idRiesgo?: number;
}

export interface GetConvocatoriasParams {
    id: string;
    cuit: number;
    idOrganismoCentral: number;
    fecha: string;
    estado: number;
    skip: number;
    take: number;
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
        const { auth }: any = api.getState();
        const refreshResult = await baseQuery(
            {
                url: '/auth/refresh-token',
                method: 'POST',
                credentials: 'include',
                body: { username: auth?.user?.usuario }
            },
            api,
            extraOptions
        );
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const periodicosApi = createApi({
    reducerPath: 'periodicosApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['ExposedEmployees'],
    endpoints: (builder) => ({
        getExposedEmployees: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/empleados-expuestos`,
                params
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),

        getEmployeesPendingPrestador: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/examenes-empleados`,
                params: {
                    ...params,
                    examen: true,
                    prestador: false
                }
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),

        getEmployeesPendingConvocated: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/examenes-empleados`,
                params: {
                    ...params,
                    examen: true,
                    prestador: true,
                    estadoGeneral: 2
                }
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),
        getEmployeesConvocated: builder.query<any, GetExposedEmployeesParams>({
            query: (params) => ({
                url: `periodicos/empleados-convocatoria`,
                params: {
                    ...params
                }
            }),
            providesTags: ['ExposedEmployees'],
            keepUnusedDataFor: 5
        }),

        getRiesgos: builder.query<Riesgo[], void>({
            query: () => `periodicos/riesgos`,
            transformResponse: (response: { data: Riesgo[] }) => response.data,
            providesTags: ['ExposedEmployees']
        }),

        getExamenesRiesgo: builder.query<Examen[], number>({
            query: (idRiesgo) => `periodicos/examenes-riesgos/${idRiesgo}`,
            transformResponse: (response: { data: Examen[] }) => response.data,
            providesTags: ['ExposedEmployees']
        }),

        getPrestadores: builder.query<
            Prestador[],
            { idProvincia?: number; idLocalidad?: number; tipo?: string }
        >({
            query: (params) => ({
                url: `periodicos/prestadores`,
                params
            }),
            transformResponse: (response: { data: Prestador[] }) => response.data,
            providesTags: ['ExposedEmployees']
        }),

        getLocalidadesPrestadores: builder.query<Localidad[], { idProvincia?: number }>({
            query: (params) => ({
                url: `periodicos/prestadores/localidades`,
                params
            }),
            transformResponse: (response: { data: { localidades: Localidad[] } }) =>
                response.data.localidades
        }),

        asignarExamenEmpleados: builder.mutation<
            any,
            { idExamen: number; payload: AsignarExamenPayload }
        >({
            query: ({ idExamen, payload }) => ({
                url: `periodicos/examen/${idExamen}/empleados`,
                method: 'POST',
                body: payload
            })
        }),

        asignarPrestadorExamenEmpleado: builder.mutation<
            any,
            { idExamenEmpleado: number; payload: AsignarPrestadorPayload }
        >({
            query: ({ idExamenEmpleado, payload }) => ({
                url: `periodicos/examen-empleado/${idExamenEmpleado}/prestador`,
                method: 'PATCH',
                body: payload
            })
        }),

        asignarPrestador: builder.mutation<
            any,
            { idExamenEmpleados: number[]; idPrestador: number }
        >({
            query: (payload) => ({
                url: `periodicos/examen-empleados/prestador`,
                method: 'PATCH',
                body: payload
            }),
            invalidatesTags: ['ExposedEmployees']
        }),
        generarConvocatorias: builder.mutation<any, { idsEmpleadosExamenes: number[] }>({
            query: (payload) => ({
                url: 'periodicos/convocatoria',
                method: 'POST',
                body: payload
            }),
            invalidatesTags: ['ExposedEmployees']
        }),
        asignarTurnoExamenEmpleados: builder.mutation<
            any,
            { idExamenEmpleados: number[]; fechaTurno: string }
        >({
            query: (payload) => ({
                url: `periodicos/examen-empleados/turno`,
                method: 'PATCH',
                body: payload
            }),
            invalidatesTags: ['ExposedEmployees']
        }),
        getAptitudes: builder.query<any[], void>({
            query: () => 'periodicos/aptitudes',
            transformResponse: (response: { data: { aptitudes: any[] } }) => response.data.aptitudes
        }),

        asignarResultadoExamenEmpleado: builder.mutation<
            any,
            {
                idExamenEmpleado: number;
                payload: {
                    idAptitud: number;
                    fechaResultado: string;
                    observaciones: string;
                    archivoResultado?: File | null;
                };
            }
        >({
            query: ({ idExamenEmpleado, payload }) => {
                const formData = new FormData();
                formData.append('idAptitud', payload.idAptitud.toString());
                formData.append('fechaResultado', payload.fechaResultado);
                formData.append('observaciones', payload.observaciones);
                if (payload.archivoResultado) {
                    formData.append('file', payload.archivoResultado);
                }

                return {
                    url: `periodicos/examen-empleado/${idExamenEmpleado}/resultado`,
                    method: 'PATCH',
                    body: formData
                };
            },
            invalidatesTags: ['ExposedEmployees']
        }),

        exportarPeriodicos: builder.mutation<
            { type: string; data: number[] },
            ExportarPeriodicosPayload
        >({
            query: (payload) => ({
                url: `periodicos/exportar`,
                method: 'POST',
                body: payload,
                responseHandler: (response) => response.json()
            })
        }),
        generarPdfResultadosConvocatoria: builder.mutation<{ data: string }, number>({
            query: (idConvocatoria) => ({
                url: `periodicos/convocatoria/${idConvocatoria}/pdf/resultados`,
                method: 'POST'
            })
        }),

        generarPdfConvocatoria: builder.mutation<{ data: string }, number>({
            query: (idConvocatoria) => ({
                url: `periodicos/convocatoria/${idConvocatoria}/pdf/convocatoria`,
                method: 'POST'
            })
        }),

        eliminarExamenEmpleado: builder.mutation<any, number>({
            query: (idExamenEmpleado) => ({
                url: `periodicos/examen-empleado/${idExamenEmpleado}`,
                method: 'DELETE'
            })
        }),
        getConvocatorias: builder.query<any, GetConvocatoriasParams>({
            query: (params) => ({
                url: `periodicos/convocatoria`,
                params
            }),
            keepUnusedDataFor: 5
        }),
        getEstablecimientosPeriodicos: builder.query<Establecimiento[], void>({
            query: () => ({
                url: 'periodicos/establecimientos'
            }),
            transformResponse: (response: { data: { establecimientos: Establecimiento[] } }) =>
                response.data.establecimientos
        })
    })
});

export const {
    useGetExposedEmployeesQuery,
    useGetEmployeesPendingPrestadorQuery,
    useGetEmployeesPendingConvocatedQuery,
    useGetEmployeesConvocatedQuery,
    useLazyGetEmployeesPendingPrestadorQuery,
    useLazyGetEmployeesPendingConvocatedQuery,
    useLazyGetEmployeesConvocatedQuery,
    useLazyGetExposedEmployeesQuery,
    useGetRiesgosQuery,
    useGetLocalidadesPrestadoresQuery,
    useLazyGetLocalidadesPrestadoresQuery,
    useGetExamenesRiesgoQuery,
    useAsignarExamenEmpleadosMutation,
    useGetPrestadoresQuery,
    useLazyGetPrestadoresQuery,
    useAsignarPrestadorMutation,
    useGenerarConvocatoriasMutation,
    useAsignarTurnoExamenEmpleadosMutation,
    useGetAptitudesQuery,
    useAsignarResultadoExamenEmpleadoMutation,
    useExportarPeriodicosMutation,
    useGenerarPdfResultadosConvocatoriaMutation,
    useGenerarPdfConvocatoriaMutation,
    useGetConvocatoriasQuery,
    useLazyGetConvocatoriasQuery,
    useGetEstablecimientosPeriodicosQuery,
    useLazyGetEstablecimientosPeriodicosQuery
} = periodicosApi;
